import { makeAutoObservable } from 'mobx'
import type {
  AnalyticsChartPeriod,
  IMessagingChartResponse,
  IMessagingStatisticResponse,
  ProcessedStatistic,
} from 'entities/Analytics'
import { formatDateForDisplay, calculateTrend, computeHasStatistic } from 'pages/analytics/lib'

export interface MessagesRechartsDataPoint {
  date: string
  name: string
  total_inbound: number
  total_outbound: number
  delivered: number
  undelivered: number
  carrier_violations: number
  optouts: number
  total_rvm: number
  delivered_rvm: number
  undelivered_rvm: number
}

export class MessagesChartDataStore {
  private _rawStatistic: IMessagingStatisticResponse[] = []
  private _rawChartData: IMessagingChartResponse[] = []
  private _period: AnalyticsChartPeriod = 'week'

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (
    response: { statistic: IMessagingStatisticResponse[]; charts: IMessagingChartResponse[] },
    period?: AnalyticsChartPeriod
  ) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    const totalOutboundStat = this._rawStatistic.find((item) => item.key === 'total_outbound')
    const totalOutbound = totalOutboundStat?.value || 0
    const prevTotalOutbound = totalOutboundStat?.prevValue || 0

    return this._rawStatistic.map((item) => {
      const { key, value, prevValue } = item
      let displayValue = value
      let displayPrevValue = prevValue

      if (
        item.key === 'delivered' ||
        item.key === 'undelivered' ||
        item.key === 'carrier_violations'
      ) {
        displayValue = totalOutbound > 0 ? Math.round((value / totalOutbound) * 100) : 0
        displayPrevValue =
          prevTotalOutbound > 0 ? Math.round((prevValue / prevTotalOutbound) * 100) : 0
      }
      const trend = calculateTrend(displayValue, displayPrevValue)

      return {
        key: key,
        label: this.getDisplayLabel(key),
        value: displayValue,
        prevValue: displayPrevValue,
        trend,
      }
    })
  }

  get rechartsData(): MessagesRechartsDataPoint[] {
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period),
        total_inbound: item.total_inbound || 0,
        total_outbound: item.total_outbound || 0,
        delivered: item.delivered || 0,
        undelivered: item.undelivered || 0,
        carrier_violations: item.carrier_violations || 0,
        optouts: item.optouts || 0,
        total_rvm: item.total_rvm || 0,
        delivered_rvm: item.delivered_rvm || 0,
        undelivered_rvm: item.undelivered_rvm || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  private getDisplayLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
      total_inbound: 'Total Inbound',
      total_outbound: 'Total Outbound',
      delivered: 'Delivery rate',
      undelivered: 'Error rate',
      carrier_violations: 'Spam rate',
      optouts: 'Opted-out',
      total_rvm: 'Total Rvm',
      delivered_rvm: 'Delivered Rvm',
      undelivered_rvm: 'Undelivered Rvm',
    }
    return labelMap[key] || key
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = 'week'
  }
}
